import React, { useState, useEffect } from 'react';
import {
  FaBoxes,
  FaShoppingCart,
  FaDollarSign,
  FaUsers,
  FaEye,
  FaExclamationTriangle,
  FaTimesCircle,
  FaClock,
  FaBoxOpen,
  FaPlus,
  FaImage,
  FaEdit,
  FaExternalLinkAlt,
  FaCheckCircle,
  FaLightbulb
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

const STORE_HEALTH_CHECKLIST = [
  { key: 'products_added', label: 'Products added', complete: true },
  { key: 'banners_uploaded', label: 'Banners uploaded', complete: true },
  { key: 'whatsapp_added', label: 'WhatsApp number added', complete: true },
  { key: 'payment_active', label: 'Payment method active', complete: false },
  { key: 'policy_pages', label: 'Policy pages completed', complete: false },
];

// TODO: no dedicated Help/Drive settings exist elsewhere — reusing the
// generic store_settings key-value pattern, same as everything else.
// Add matching admin fields for these two keys in StoreSettings.jsx if
// you want them editable there (snippet provided separately).
const DEFAULT_STORE_TIP = 'Products with clear photos, size options, and simple descriptions usually get more orders.';

const Dashboard = () => {
  const [stats, setStats] = useState({
    products: 0,
    orders: 0,
    revenue: 0,
    customers: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [outOfStockProducts, setOutOfStockProducts] = useState([]);
  const [storeTip, setStoreTip] = useState(DEFAULT_STORE_TIP);
  const [helpVideosUrl, setHelpVideosUrl] = useState('');
  const [helpVideoEmbedUrl, setHelpVideoEmbedUrl] = useState(''); // <-- ADD THIS
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    loadDashboardSettings();
  }, []);

  const loadDashboardSettings = async () => {
    try {
      const globalRes = await fetch('https://storii.com.pk/main-api/get-global-settings.php');
      const globalData = await globalRes.json();

      if (globalData.success && globalData.settings) {
        if (globalData.settings.store_tip) setStoreTip(globalData.settings.store_tip);

        // NEW: Parse the YouTube URL into an embed URL
        if (globalData.settings.help_video_url) {
          const embedUrl = getYouTubeEmbedUrl(globalData.settings.help_video_url);
          setHelpVideoEmbedUrl(embedUrl);
        }
      }

      const response = await fetch('https://demo-site.storii.com.pk/demo-api/public/get-settings.php');
      const data = await response.json();
      if (data.success && data.settings) {
        if (!globalData.settings?.store_tip && data.settings.dashboard_store_tip) {
          setStoreTip(data.settings.dashboard_store_tip);
        }
        if (data.settings.help_videos_url) setHelpVideosUrl(data.settings.help_videos_url);
      }
    } catch (error) {
      console.error('Error loading dashboard settings:', error);
    }
  };

  // Extract YouTube ID from any URL format and return embed URL
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    try {
      // youtu.be/ID
      const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
      if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}`;

      // youtube.com/watch?v=ID
      const watchMatch = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
      if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}`;

      // youtube.com/embed/ID (already correct)
      const embedMatch = url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/);
      if (embedMatch) return `https://www.youtube.com/embed/${embedMatch[1]}`;

      // youtube.com/shorts/ID
      const shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
      if (shortsMatch) return `https://www.youtube.com/embed/${shortsMatch[1]}`;

      return '';
    } catch (e) {
      return '';
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const productsResponse = await fetch('https://demo-site.storii.com.pk/demo-api/products.php');
      const productsData = await productsResponse.json();

      const ordersResponse = await fetch('https://demo-site.storii.com.pk/demo-api/admin/get-orders.php');
      const ordersData = await ordersResponse.json();

      const lowStockResponse = await fetch('https://demo-site.storii.com.pk/demo-api/admin/get-low-stock-products.php');
      const lowStockData = await lowStockResponse.json();

      // FIXED: both updates now use the functional setState form, so
      // neither one can silently overwrite the other with a stale value
      // from the render closure — this was the actual cause of "Products
      // Live" showing 0 despite real active products existing.
      if (productsData.success) {
        setStats(prev => ({ ...prev, products: productsData.products?.length || 0 }));
      }

      if (ordersData.success) {
        setStats(prev => ({
          ...prev,
          orders: ordersData.stats.total_orders || 0,
          revenue: ordersData.stats.total_revenue || 0,
          customers: ordersData.stats.total_customers || 0,
          pending_count: ordersData.stats.pending_count ?? 0
        }));
        setRecentOrders(ordersData.stats.recent_orders || []);
      }

      if (lowStockData.success) {
        setLowStockProducts(lowStockData.low_stock || []);
        setOutOfStockProducts(lowStockData.out_of_stock || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Real total now, from get-orders.php — falls back to the old
  // recent-5-only estimate only if the API hasn't been updated yet.
  const pendingOrdersCount = stats.pending_count ?? recentOrders.filter(o => (o.status || 'pending') === 'pending').length;
  const PRODUCT_LIMIT = 25;

  const statCards = [
    {
      title: 'Total Orders',
      value: stats.orders,
      sub: 'All-time orders',
      icon: <FaShoppingCart />,
      iconBg: '#fdece8',
      iconColor: '#e8604c',
      link: '/admin/orders'
    },
    {
      title: 'Pending Orders',
      value: pendingOrdersCount,
      sub: 'Need confirmation',
      icon: <FaClock />,
      iconBg: '#fdf1d6',
      iconColor: '#a3760a',
      // TODO: Orders.jsx needs to read this ?status= param on mount and
      // pre-set its status filter accordingly — I need to see the current
      // Orders.jsx to wire that safely (it's gone through several rewrites
      // this session and I don't have the latest version).
      link: '/admin/orders?status=pending'
    },
    {
      title: 'Total Revenue',
      value: `PKR ${Number(stats.revenue).toLocaleString()}`,
      sub: 'Completed orders',
      icon: <FaDollarSign />,
      iconBg: '#dcf5e3',
      iconColor: '#1e8a4c',
      // TODO: this now LINKS to a completed-only filter, but the number
      // shown still comes from ordersData.stats.total_revenue, which I
      // believe currently sums ALL orders regardless of status (that's
      // computed server-side in get-orders.php, which I don't have the
      // current version of). Need that file to confirm/fix the actual
      // revenue calculation, not just where the click navigates.
      link: '/admin/orders?status=delivered'
    },
    {
      title: 'Products Live',
      value: `${stats.products} / ${PRODUCT_LIMIT}`,
      sub: 'MVP product limit',
      icon: <FaBoxes />,
      iconBg: '#dbeafe',
      iconColor: '#1d4ed8',
      link: '/admin/products'
    },
    {
      title: 'Low Stock',
      value: lowStockProducts.length,
      sub: 'Action needed',
      icon: <FaExclamationTriangle />,
      iconBg: '#fbdede',
      iconColor: '#c8342a',
      // TODO: same as Pending Orders — Products.jsx needs to read this
      // param and pre-apply a low-stock filter. Need current Products.jsx.
      link: '/admin/products?filter=low_stock'
    },
  ];

  const quickActions = [
    {
      label: 'Add Product',
      icon: <FaPlus />,
      // TODO: still points at the OLD AddProduct.jsx page, per your note.
      // You want this to look/behave like EditProduct.jsx's drawer instead
      // (same fields: Sale Price, Status, Product Tags, Product Content,
      // Size Guide, etc.) in "create" mode. That's a real merge of two
      // components, not a one-line fix — I need both AddProduct.jsx and
      // EditProduct.jsx's current state, plus whichever PHP endpoint
      // currently handles product creation, to do this safely.
      onClick: () => navigate('/admin/add-product')
    },
    { label: 'View Orders', icon: <FaEye />, onClick: () => navigate('/admin/orders') },
    {
      label: 'Update Banner',
      icon: <FaImage />,
      // Now points at Storefront.jsx (Header Banners section), matching
      // where banner management actually lives now — replaces the old
      // Hero Slider settings page link.
      onClick: () => navigate('/admin/store-front')
    },
    { label: 'Request Change', icon: <FaEdit />, /* onClick: () => alert('Request Change flow not wired yet — let me know how this should work.') */ },
    {
      label: 'Visit Store',
      icon: <FaExternalLinkAlt />,
      onClick: () => window.open('https://demo-site.storii.com.pk/', '_blank')
    },
  ];

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      processing: 'status-confirmed',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
      completed: 'status-delivered',
      cancelled: 'status-cancelled'
    };
    return statusClasses[status] || 'status-default';
  };

  const healthCompleteCount = STORE_HEALTH_CHECKLIST.filter(i => i.complete).length;
  const healthPercent = Math.round((healthCompleteCount / STORE_HEALTH_CHECKLIST.length) * 100);

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-welcome">
        <h1>Welcome back, <span className="highlight">Admin</span></h1>
        <p>Here's what's happening with your online store today.</p>
      </div>

      <div className="stats-grid">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className="stat-card"
            onClick={() => stat.link && navigate(stat.link)}
          >
            <div className="stat-icon-circle" style={{ background: stat.iconBg, color: stat.iconColor }}>
              {stat.icon}
            </div>
            <div className="stat-info">
              <p className="stat-title">{stat.title}</p>
              <h3 className="stat-value">{stat.value}</h3>
              <p className="stat-sub">{stat.sub}</p>
            </div>
          </div>
        ))}
      </div>

      <h2 className="section-title">Quick Actions</h2>
      <div className="quick-actions-grid">
        {quickActions.map((action, index) => (
          <div key={index} className="quick-action-card" onClick={action.onClick}>
            <div className="quick-action-icon">{action.icon}</div>
            <span>{action.label}</span>
          </div>
        ))}
      </div>

      <div className="dashboard-bottom-grid">

        <div className="dashboard-bottom-left">

          {(lowStockProducts.length > 0 || outOfStockProducts.length > 0) && (
            <div className="panel low-stock-section">
              <div className="low-stock-header">
                <h2>
                  <FaExclamationTriangle className="alert-icon" />
                  Inventory Alerts
                </h2>
                <button onClick={() => navigate('/admin/products')} className="dark-btn">
                  Manage Products
                </button>
              </div>

              {outOfStockProducts.length > 0 && (
                <div className="out-of-stock">
                  <h3>
                    <FaTimesCircle className="out-icon" />
                    Out of Stock ({outOfStockProducts.length})
                  </h3>
                  <div className="low-stock-table-container">
                    <table className="low-stock-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Price</th>
                          <th>Stock</th>
                          <th>Category</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {outOfStockProducts.map(product => (
                          <tr key={product.id}>
                            <td>
                              <div className="product-info">
                                {/* 
                                <img
                                  src={product.image || 'https://via.placeholder.com/40'}
                                  alt={product.name}
                                  className="product-thumb-small"
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/40?text=No+Img';
                                  }}
                                />
                                */}
                                <span>{product.name}</span>
                              </div>
                            </td>
                            <td>PKR {parseFloat(product.price).toLocaleString()}</td>
                            <td><span className="out-badge">0</span></td>
                            <td>{product.category}</td>
                            <td>
                              <button
                                className="stock-action-btn"
                                onClick={() => navigate(`/admin/edit-product/${product.id}`)}
                              >
                                Restock
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {lowStockProducts.length > 0 && (
                <div className="low-stock">
                  <h3>
                    <FaBoxOpen className="low-icon" />
                    Low Stock ({lowStockProducts.length})
                  </h3>
                  <div className="low-stock-table-container">
                    <table className="low-stock-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Price</th>
                          <th>Stock</th>
                          <th>Threshold</th>
                          <th>Category</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lowStockProducts.map(product => (
                          <tr key={product.id}>
                            <td>
                              <div className="product-info">
                                {/* 
                                <img
                                  src={product.image || 'https://via.placeholder.com/40'}
                                  alt={product.name}
                                  className="product-thumb-small"
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/40?text=No+Img';
                                  }}
                                />
                                */}
                                <span>{product.name}</span>
                              </div>
                            </td>
                            <td>PKR {parseFloat(product.price).toLocaleString()}</td>
                            <td><span className="low-badge">{product.stock}</span></td>
                            <td>{product.low_stock_threshold}</td>
                            <td>{product.category}</td>
                            <td>
                              <button
                                className="stock-action-btn"
                                onClick={() => navigate(`/admin/edit-product/${product.id}`)}
                              >
                                Restock
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {recentOrders.length > 0 && (
            <div className="panel recent-orders">
              <div className="panel-header">
                <div>
                  <h2>Recent Orders</h2>
                  <p className="panel-sub">Latest {recentOrders.length} orders from your storefront</p>
                </div>
                {stats.orders > 5 && (
                  <button onClick={() => navigate('/admin/orders')} className="view-all-link">
                    View all &rarr;
                  </button>
                )}
              </div>
              <div className="orders-table-container">
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Customer</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOrders.map(order => (
                      <tr key={order.id}>
                        <td className="order-id-cell">{order.order_number}</td>
                        <td>{order.customer_name}</td>
                        <td>{new Date(order.created_at).toLocaleDateString()}</td>
                        <td>PKR {parseFloat(order.total_amount).toLocaleString()}</td>
                        <td>
                          <span className={`status-pill ${getStatusBadgeClass(order.status)}`}>
                            {order.status || 'pending'}
                          </span>
                        </td>
                        <td>
                          <button className="view-order-btn" onClick={() => navigate(`/admin/orders/${order.id}`)}>
                            <FaEye /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="dashboard-bottom-right">

          <div className="panel store-health-panel">
            <div className="panel-header">
              <h2>Store Health</h2>
              <span className="health-score">{healthCompleteCount}/{STORE_HEALTH_CHECKLIST.length}</span>
            </div>
            <div className="health-bar-track">
              <div className="health-bar-fill" style={{ width: `${healthPercent}%` }}></div>
            </div>
            <ul className="health-list">
              {STORE_HEALTH_CHECKLIST.map(item => (
                <li key={item.key} className="health-item">
                  <span className="health-item-label">
                    {item.complete ? (
                      <FaCheckCircle className="health-icon-done" />
                    ) : (
                      <FaExclamationTriangle className="health-icon-pending" />
                    )}
                    {item.label}
                  </span>
                  <span className={item.complete ? 'health-status-complete' : 'health-status-pending'}>
                    {item.complete ? 'Complete' : 'Pending'}
                  </span>
                </li>
              ))}
            </ul>
            <p className="health-note">Complete these items to make your store more trustworthy for customers.</p>
          </div>

          {/* Store Tip — now dynamic, editable in StoreSettings.jsx (see
              snippet below) instead of hardcoded text. Falls back to the
              original wording if nothing's been saved yet. */}
          {/* ===== STORE TIP CARD (text only) ===== */}
          <div className="store-tip-card">
            <div className="store-tip-label"><FaLightbulb /> STORE TIP</div>
            <p>{storeTip}</p>
          </div>

          {/* ===== HELP VIDEO CARD (video only) ===== */}
          {(helpVideoEmbedUrl || helpVideosUrl) && (
            <div className="help-video-card">
              <div className="help-video-label">🎥 HELP VIDEO</div>

              {helpVideoEmbedUrl ? (
                <div className="help-video-embed">
                  <iframe
                    src={helpVideoEmbedUrl}
                    title="Store Help Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              ) : (
                <button
                  className="help-video-btn"
                  onClick={() => window.open(helpVideosUrl, '_blank')}
                >
                  Watch Help Video
                </button>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Dashboard;